import type { PublicChatCta } from './types';
import { calendlyEmbedUrl, DEMO_CALENDLY_URL, SIGNUP_URL } from './constants';
import { useChatEmbed } from './ChatEmbedContext';

type Props = {
  cta: PublicChatCta;
};

/**
 * Renders a single primary action from the API (demo / signup / lead form).
 * Falls back to spec defaults when `label` or `url` are missing.
 */
export function CTAButton({ cta }: Props) {
  const embed = useChatEmbed();
  const type = (cta.type || '').toLowerCase();
  const action = (cta.action || '').toLowerCase();

  if (type === 'none') return null;

  const isLead = type === 'lead_form' || action === 'open_form';
  const hasUrl = Boolean(cta.url && /^https?:\/\//i.test(cta.url));

  if (type !== 'demo' && type !== 'signup' && !isLead && !hasUrl) return null;

  const handleClick = () => {
    if (type === 'demo') {
      const href =
        cta.url && /^https?:\/\//i.test(cta.url) ? cta.url : DEMO_CALENDLY_URL;
      const embedded = calendlyEmbedUrl(href);
      if (embed) {
        embed.openEmbeddedUrl(embedded);
        return;
      }
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (type === 'signup') {
      const href =
        cta.url && /^https?:\/\//i.test(cta.url) ? cta.url : SIGNUP_URL;
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isLead) {
      window.dispatchEvent(new Event('open-lead-form'));
      return;
    }
    if (cta.url?.startsWith('http')) {
      window.open(cta.url, '_blank', 'noopener,noreferrer');
    }
  };

  let label = cta.label?.trim();
  if (!label) {
    if (type === 'demo') label = 'Book a Demo';
    else if (type === 'signup') label = 'Get Started';
    else if (isLead) label = 'Get in Touch';
    else label = 'Learn more';
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="mt-2.5 inline-flex w-full max-w-full items-center justify-center gap-1.5 rounded-full bg-neutral-950 py-2.5 text-[13px] font-medium text-white shadow-md shadow-neutral-900/15 transition hover:bg-neutral-800 active:scale-[0.99] sm:mt-3 sm:gap-2 sm:py-2.5 sm:text-[14px]"
    >
      <i
        className={`text-base opacity-90 ${type === 'demo' ? 'ri-calendar-line' : 'ri-external-link-line'}`}
        aria-hidden
      />
      {label}
    </button>
  );
}
